/* eslint-disable @atlaskit/platform/ensure-feature-flag-prefix */
import { useCallback, useMemo, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { setInteractionError } from '@atlaskit/react-ufo/set-interaction-error';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { ExperiencePerformanceTypes, ExperienceTypes, UFOExperience } from '@atlassian/ufo';

import { QUICK_FIND_POST_QUERY_INTERACTION } from '../../../../common/constants/quick-find';
import { onSearchRequestCompleted } from '../../../../common/utils/quick-find/events/search-request-completed';
import { useIsHello, usePrimaryProduct, useSearchActions } from '../../../store';
import { useUserShardModel } from '../../../store/bootstrap';
import { useQuickFindAnalytics, useQuickFindAttributes } from '../../analytics';
export const DEBOUNCE_TIMEOUT = 250;

export const useQuickFindResultsCallbacks = () => {
	const [primaryProduct] = usePrimaryProduct();
	const serveUserShardModel = useUserShardModel();
	const { incrementQueryVersion } = useSearchActions();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const [isHello] = useIsHello();

	const ufoPreQueryLoadExperience = useRef<UFOExperience>(
		new UFOExperience(`${primaryProduct}-pre-query-results-load`, {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
			platform: { component: 'search-dialog' },
		}),
	);

	const ufoPostQueryLoadExperience = useRef<UFOExperience>(
		new UFOExperience(`${primaryProduct}-post-query-results-load`, {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
			platform: { component: 'search-dialog' },
		}),
	);

	const onPreQueryStart = useCallback(() => {
		ufoPreQueryLoadExperience.current.start();
	}, []);

	const onPreQuerySuccess = useCallback(() => {
		ufoPreQueryLoadExperience.current.success({
			metadata: { isQuickFind: true },
		});
	}, []);

	const onPreQueryFailure = useCallback(() => {
		ufoPreQueryLoadExperience.current.failure();
	}, []);

	const onPostQueryChange = useCallback(() => {
		const useUfoMetrics = fg('quick_find_metrics_using_ufo_react');
		const useUfoPostQueryMetricInsideDebounce = fg('quick-find-post-query-metric-inside-debounce');
		if (useUfoMetrics && !useUfoPostQueryMetricInsideDebounce) {
			traceUFOPress(QUICK_FIND_POST_QUERY_INTERACTION);
		}
	}, []);

	const onPostQueryStart = useCallback(() => {
		const useUfoMetrics = fg('quick_find_metrics_using_ufo_react');
		const useUfoPostQueryMetricInsideDebounce = fg('quick-find-post-query-metric-inside-debounce');
		/**
		 * QS-7380 (LDR: https://hello.atlassian.net/wiki/x/QVo8QwE)
		 * Aborting on keypress fires a lot of dirty data volume to ufo. Instead we only instrument on query (inside the debounce), offsetting the start time by the debounce window because the keypress that trigger the debounce is the start of the interaction.
		 * fg('quick-find-post-query-metric-inside-debounce')
		 */
		if (useUfoMetrics && useUfoPostQueryMetricInsideDebounce) {
			const startTimestamp = performance.now() - DEBOUNCE_TIMEOUT;
			traceUFOPress(QUICK_FIND_POST_QUERY_INTERACTION, startTimestamp);
		}
		// ufo-v1 (above is react-ufo)
		const restartMetric = async () => {
			await ufoPostQueryLoadExperience.current.abort();
			await ufoPostQueryLoadExperience.current.start();
		};
		restartMetric();
	}, []);

	const onPostQuerySuccess = useCallback(
		(duration: number, hasThirdPartyResults: boolean) => {
			incrementQueryVersion();

			ufoPostQueryLoadExperience.current.success({
				metadata: {
					isQuickFind: true,
					isHello,
					use3PResults: serveUserShardModel,
					hasThirdPartyResults,
				},
			});

			fireAnalyticsEvent(
				onSearchRequestCompleted({
					attributes: {
						...commonAttributes,
						postQueryRequestDurationMs: duration,
						hasThirdPartyResults,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[
			commonAttributes,
			fireAnalyticsEvent,
			incrementQueryVersion,
			nonPrivacySafeAttributes,
			isHello,
			serveUserShardModel,
		],
	);

	const onPostQueryFailure = useCallback(() => {
		ufoPostQueryLoadExperience.current.failure();

		if (fg('quick_find_metrics_using_ufo_react')) {
			setInteractionError(QUICK_FIND_POST_QUERY_INTERACTION, {
				errorMessage: 'request failed',
				name: 'request failed',
			});
		}
	}, []);

	const onPostQueryCancel = useCallback(() => {
		ufoPostQueryLoadExperience.current.abort();
	}, []);

	return useMemo(
		() => ({
			onPreQueryStart,
			onPreQuerySuccess,
			onPreQueryFailure,
			onPostQueryChange,
			onPostQueryStart,
			onPostQuerySuccess,
			onPostQueryFailure,
			onPostQueryCancel,
		}),
		[
			onPostQueryCancel,
			onPostQueryFailure,
			onPostQueryChange,
			onPostQueryStart,
			onPostQuerySuccess,
			onPreQueryFailure,
			onPreQueryStart,
			onPreQuerySuccess,
		],
	);
};
